import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { startCase } from 'lodash';
import useMeasure from 'react-use-measure';

import { ModuleBaseFloor } from '@blocks';
import { getAnalysisParameterValue, Unit } from '@core';
import { useAnalysisById } from '@hooks';
import { useTranslate } from '@i18n';
import { useActivityReport, useLayoutCustomizations, useUiParameters } from '@queries';
import {
    activeAnalysisAtom,
    convertedLayoutCustomCorridorsMapAtom,
    corridorsAtom,
    currentActivityIdAtom,
    layoutAtom,
    userSettingsAtom,
} from '@store';
import { Box, colors, FloorParameterCard, Spinner } from '@ui';

import * as css from './CustomModule.css';

type CustomModuleProps = {
    nodeId: string;
};

const MIN_CARD_WIDTH = 238;
const GRID_GAP = 8;
const MIN_MODULE_WIDTH = MIN_CARD_WIDTH + GRID_GAP;
const IDEAL_MODULE_WIDTH = 250;
const IDEAL_MODULE_HEIGHT = 176;
const CARD_HEIGHT_NO_CORRIDORS = 136;

export const CustomModule = ({ nodeId }: CustomModuleProps) => {
    const t = useTranslate();
    const [ref, { width, height }] = useMeasure({ debounce: 100 });
    const layout = useAtomValue(layoutAtom);
    const userSettings = useAtomValue(userSettingsAtom);
    const activityId = useAtomValue(currentActivityIdAtom);
    const activeSwingAnalysis = useAtomValue(activeAnalysisAtom);
    const corridors = useAtomValue(corridorsAtom);
    const comparisonSwingAnalysis = useAnalysisById(userSettings.comparisonSwingId);
    const { data: activityReport } = useActivityReport(activityId);
    const { data: customizations, isLoading } = useLayoutCustomizations(layout?.id || -1);
    const customCorridorMap = useAtomValue(convertedLayoutCustomCorridorsMapAtom);
    const { data: parameters } = useUiParameters();
    const derivedIdealModuleHeight =
        userSettings.showCorridors || userSettings.showGoal ? CARD_HEIGHT_NO_CORRIDORS : IDEAL_MODULE_HEIGHT;
    const dimensions = useMemo(() => {
        if (!width || !height) return { columns: 0, rows: 0, maxItems: 0 };

        let columns = Math.max(1, Math.floor(width / IDEAL_MODULE_WIDTH));
        let rows = Math.max(1, Math.floor(height / derivedIdealModuleHeight));

        if (columns === 1) {
            columns = Math.max(1, Math.floor((width + GRID_GAP) / MIN_MODULE_WIDTH));
            rows = Math.max(1, Math.floor(height / derivedIdealModuleHeight));
        }

        return {
            columns,
            rows,
            maxItems: columns * rows,
        };
    }, [width, height]);

    const parameterIds = useMemo(
        () =>
            customizations
                ?.filter(
                    (customization) =>
                        customization.parentUiNodeId === nodeId && customization.showChildOn?.includes('floor'),
                )
                .map(({ childUiNodeId }) => childUiNodeId),
        [customizations, nodeId],
    );

    const parameterNodes = useMemo(
        () => parameters?.filter((parameter) => parameterIds?.includes(parameter.id)),
        [parameterIds, parameters],
    );

    const title = useMemo(() => startCase(nodeId.split('.')?.[1]) ?? 'Custom Module', [nodeId]);
    const renderNodes = useMemo(() => parameterNodes?.slice(0, dimensions.maxItems), [parameterNodes, dimensions]);
    const style = useMemo(
        () => ({
            gridTemplateColumns: `repeat(${dimensions.columns}, 1fr)`,
            gridTemplateRows: `repeat(${dimensions.rows}, 1fr)`,
        }),
        [dimensions.columns, dimensions.rows],
    );

    const selectedActiveAnalysis = activeSwingAnalysis?.fullAnalysis || activeSwingAnalysis?.quickAnalysis;
    const isReady = !(dimensions.maxItems === 0 || isLoading);
    return (
        <ModuleBaseFloor title={title} subtitle={t('module_type.custom')} ref={ref}>
            {isReady ? (
                <div className={css.customModuleLayout} style={style}>
                    {renderNodes?.map(({ i18n, parameter }) => {
                        const paramValue = getAnalysisParameterValue(selectedActiveAnalysis, parameter.name);
                        const comparisonParamValue = getAnalysisParameterValue(comparisonSwingAnalysis, parameter.name);
                        const reportValue = activityReport?.[parameter.name];
                        const paramCorridors = corridors?.[parameter.name];
                        const goalValue = customCorridorMap?.[parameter.smpId];

                        return (
                            <FloorParameterCard
                                key={parameter.smpId}
                                title={t(i18n ?? '-')}
                                unit={parameter.unit as Unit}
                                value={paramValue}
                                mean={reportValue?.mean}
                                stdDev={reportValue?.stdDev}
                                userPreferredUnit={userSettings.userPreferredUnit}
                                corridorParameterMargins={userSettings.showCorridors ? paramCorridors : null}
                                comparisonValue={userSettings.showComparison ? comparisonParamValue : null}
                                goalCorridor={userSettings.showGoal ? goalValue : null}
                            />
                        );
                    })}
                </div>
            ) : (
                <Box display="flex" width="100%" height="100%" justifyContent="center" alignItems="center">
                    <Spinner size="l" color={colors.bluegray[600]} />
                </Box>
            )}
        </ModuleBaseFloor>
    );
};
